import { type FC } from 'react';
import cn from 'classnames';
import { Cell, type TestProps, TextCell, useHStack } from '@direct-frontend/components';
import { type OpenApiSchemaProductVariantListItem } from '../../../../api/schema';
import { getGroupedCharacteristics, getGroupedCharacteristicsLine } from '../../../../lib/characteristics';
import { SCROLL_TO_ELEMENTS_MAP } from '../../../../lib/hocs';
import { productVariantRoute } from '../../../../lib/routes';
import { MediaPreview } from '../../../MediaPreview';
import styles from './CellVariantName.module.css';

export type CellVariantNameMenu = FC<{
    variant: OpenApiSchemaProductVariantListItem;
}>;

type CellVariantNameProps = {
    variant: OpenApiSchemaProductVariantListItem;
    className?: string;
    onClick?: () => void;
    Menu?: CellVariantNameMenu;
    clickableCaption?: boolean;
    containerTestId?: string;
} & TestProps;

export const CellVariantName: FC<CellVariantNameProps> = ({
    variant,
    'data-testid': dataTestId = 'CellVariantName',
    onClick,
    Menu,
    className,
    clickableCaption = true,
    containerTestId = dataTestId,
}) => {
    const {
        name,
        media_items: media,
        seller_sku: sellerSku,
        characteristics,
        product_id: productId,
        id,
        status,
    } = variant;

    const hStackClassName = useHStack({
        alignItems: 'flex-start',
        justifyContent: 'space-between',
    });

    const hasGroupedCharacteristics = Boolean(getGroupedCharacteristics(characteristics));

    const captionHref =
        status !== 'ARCHIVED' && hasGroupedCharacteristics
            ? productVariantRoute.getUrl({
                  params: {
                      variantId: id,
                      productId: productId,
                  },
                  hash: SCROLL_TO_ELEMENTS_MAP.variants,
              })
            : undefined;

    return (
        <div
            data-testid={containerTestId}
            data-variant-id={id}
            className={cn(styles.container, hStackClassName, className)}
        >
            <Cell type="Image" isGrowable={false} rightPadding={false} data-testid={dataTestId} onClick={onClick}>
                <MediaPreview
                    borderRadius="8"
                    background="gray"
                    fit="contain"
                    loading="lazy"
                    height="52"
                    width="52"
                    media={media}
                    alt="Изображение товара"
                    withZoomPopup
                    zoomHeight="200"
                    zoomWidth="200"
                    zoomPlacement={['right-start', 'right-end']}
                />
            </Cell>
            <TextCell
                className={styles.text}
                title={name}
                caption={getGroupedCharacteristicsLine(sellerSku, characteristics)}
                captionHref={clickableCaption ? captionHref : undefined}
                data-testid={`${dataTestId}.Info`}
                isGrowable
                onClick={onClick}
                iconRight={Menu ? <Menu variant={variant} /> : undefined}
            />
        </div>
    );
};
